import React, { useState, useCallback } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    TextField, 
    Button, 
    CircularProgress,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    IconButton,
    Grid,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudIcon from '@mui/icons-material/Cloud';

const BranchCreateModal = ({ open, onClose, onSave, isSaving = false }) => {
    // Common styles for compact input fields
    const compactInputStyle = {
        '& .MuiInputBase-root': { 
            height: '32px',
            fontSize: '0.875rem'
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
        }
    };

    const [formData, setFormData] = useState({
        Code: '',
        Name: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        StateCodeId: '',
        ContactNumber1: '',
        ContactNumber2: '',
        ContactNumber3: '',
        Email: '',
        GSTNumber: '',
        PanNumber: '',
        PaymentBankId: '',
        BToBPrefix: '',
        BToCPrefix: '',
        IGSTPrefix: '',
        IGSTCustomerPrefix: '',
        CashOpeningBalance: '',
        CashOpeningDate: '',
        StockOpeningBalance: '',
        StockOpeningDate: '',
        IsEInvoicingEnabled: false,
        IsEWaybillEnabled: false,
        EinvoicePortalUserName: '',
        EinvoicePortalPassword: '',
        myGSTcafeCustomerID: '',
        myGSTcafeApiID: '',
        myGSTcafeApiSecret: ''
    });

    const [expandedSections, setExpandedSections] = useState({
        basic: true,
        address: false,
        contact: false,
        tax: false,
        billing: false,
        balances: false,
        einvoicing: false
    });

    const [validationErrors, setValidationErrors] = useState({});

    const validateForm = useCallback(() => {
        const errors = {};
        
        if (!formData.Code?.trim()) {
            errors.Code = 'Branch code is required';
        }
        
        if (!formData.Name?.trim()) {
            errors.Name = 'Branch name is required';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData.Code, formData.Name]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [validationErrors]);

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    const handleClose = useCallback(() => {
        setFormData({
            Code: '',
            Name: '',
            AddressLine1: '',
            AddressLine2: '',
            Location: '',
            PinCode: '',
            StateCodeId: '',
            ContactNumber1: '',
            ContactNumber2: '',
            ContactNumber3: '',
            Email: '',
            GSTNumber: '',
            PanNumber: '',
            PaymentBankId: '',
            BToBPrefix: '',
            BToCPrefix: '',
            IGSTPrefix: '',
            IGSTCustomerPrefix: '',
            CashOpeningBalance: '',
            CashOpeningDate: '',
            StockOpeningBalance: '',
            StockOpeningDate: '',
            IsEInvoicingEnabled: false,
            IsEWaybillEnabled: false,
            EinvoicePortalUserName: '',
            EinvoicePortalPassword: '',
            myGSTcafeCustomerID: '',
            myGSTcafeApiID: '',
            myGSTcafeApiSecret: ''
        });
        setExpandedSections({
            basic: true,
            address: false,
            contact: false,
            tax: false,
            billing: false,
            balances: false,
            einvoicing: false
        });
        setValidationErrors({});
        onClose();
    }, [onClose]);

    const handleAccordionChange = useCallback((section) => (_, isExpanded) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: isExpanded
        }));
    }, []);

    const isFormValid = !!(formData.Code && formData.Name) && Object.keys(validationErrors).length === 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6">Create New Branch</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={1} mt={1}>
                    
                    {/* Basic Information */}
                    <Accordion expanded={expandedSections.basic} onChange={handleAccordionChange('basic')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <BusinessIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                                <Typography variant="subtitle1" fontWeight="600">
                                    Basic Information
                                </Typography>
                                <Chip label="Required" size="small" color="error" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="Code"
                                        value={formData.Code}
                                        onChange={handleChange}
                                        label="Branch Code"
                                        placeholder="e.g., BR001"
                                        required
                                        inputProps={{ maxLength: 5 }}
                                        error={!!validationErrors.Code}
                                        helperText={validationErrors.Code}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="Name"
                                        value={formData.Name}
                                        onChange={handleChange}
                                        label="Branch Name"
                                        placeholder="Enter branch name"
                                        required
                                        inputProps={{ maxLength: 150 }}
                                        error={!!validationErrors.Name}
                                        helperText={validationErrors.Name}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Address Details */}
                    <Accordion expanded={expandedSections.address} onChange={handleAccordionChange('address')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon sx={{ fontSize: 20, color: '#388e3c' }} />
                                <Typography variant="subtitle1" fontWeight="600">Address Details</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="AddressLine1"
                                        value={formData.AddressLine1}
                                        onChange={handleChange}
                                        label="Address Line 1"
                                        placeholder="Street address"
                                        inputProps={{ maxLength: 200 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="AddressLine2"
                                        value={formData.AddressLine2}
                                        onChange={handleChange}
                                        label="Address Line 2"
                                        placeholder="Apartment, suite"
                                        inputProps={{ maxLength: 200 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="Location"
                                        value={formData.Location}
                                        onChange={handleChange}
                                        label="City"
                                        placeholder="City name"
                                        inputProps={{ maxLength: 50 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="PinCode"
                                        value={formData.PinCode}
                                        onChange={handleChange}
                                        label="Pin Code"
                                        placeholder="123456"
                                        inputProps={{ maxLength: 6 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="StateCodeId"
                                        type="number"
                                        value={formData.StateCodeId}
                                        onChange={handleChange}
                                        label="State Code ID"
                                        placeholder="State ID"
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Contact Details */}
                    <Accordion expanded={expandedSections.contact} onChange={handleAccordionChange('contact')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <ContactPhoneIcon sx={{ fontSize: 20, color: '#f57c00' }} />
                                <Typography variant="subtitle1" fontWeight="600">Contact Details</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="ContactNumber1"
                                        value={formData.ContactNumber1}
                                        onChange={handleChange}
                                        label="Primary Contact"
                                        placeholder="+91 XXXXX XXXXX"
                                        inputProps={{ maxLength: 15 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="ContactNumber2"
                                        value={formData.ContactNumber2}
                                        onChange={handleChange}
                                        label="Secondary Contact"
                                        placeholder="+91 XXXXX XXXXX"
                                        inputProps={{ maxLength: 15 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="ContactNumber3"
                                        value={formData.ContactNumber3}
                                        onChange={handleChange}
                                        label="Alternate Contact"
                                        placeholder="+91 XXXXX XXXXX"
                                        inputProps={{ maxLength: 15 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="Email"
                                        type="email"
                                        value={formData.Email}
                                        onChange={handleChange}
                                        label="Email Address"
                                        placeholder="branch@company.com"
                                        inputProps={{ maxLength: 100 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Tax Details */}
                    <Accordion expanded={expandedSections.tax} onChange={handleAccordionChange('tax')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReceiptIcon sx={{ fontSize: 20, color: '#d32f2f' }} />
                                <Typography variant="subtitle1" fontWeight="600">Tax Details</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="GSTNumber"
                                        value={formData.GSTNumber}
                                        onChange={handleChange}
                                        label="GST Number"
                                        placeholder="22AAAAA0000A1Z5"
                                        inputProps={{ maxLength: 15 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="PanNumber"
                                        value={formData.PanNumber}
                                        onChange={handleChange}
                                        label="PAN Number"
                                        placeholder="AAAAA0000A"
                                        inputProps={{ maxLength: 10 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="PaymentBankId"
                                        type="number"
                                        value={formData.PaymentBankId}
                                        onChange={handleChange}
                                        label="Payment Bank ID"
                                        placeholder="Bank ID"
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Bill Prefix Details */}
                    <Accordion expanded={expandedSections.billing} onChange={handleAccordionChange('billing')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PaymentIcon sx={{ fontSize: 20, color: '#7b1fa2' }} />
                                <Typography variant="subtitle1" fontWeight="600">Bill Prefix Details</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="BToBPrefix"
                                        value={formData.BToBPrefix}
                                        onChange={handleChange}
                                        label="B2B Prefix"
                                        placeholder="B2B"
                                        inputProps={{ maxLength: 5 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="BToCPrefix"
                                        value={formData.BToCPrefix}
                                        onChange={handleChange}
                                        label="B2C Prefix"
                                        placeholder="B2C"
                                        inputProps={{ maxLength: 5 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="IGSTPrefix"
                                        value={formData.IGSTPrefix}
                                        onChange={handleChange}
                                        label="IGST Prefix"
                                        placeholder="IGST"
                                        inputProps={{ maxLength: 5 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="IGSTCustomerPrefix"
                                        value={formData.IGSTCustomerPrefix}
                                        onChange={handleChange}
                                        label="IGST Customer"
                                        placeholder="IGSTC"
                                        inputProps={{ maxLength: 5 }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Opening Balances */}
                    <Accordion expanded={expandedSections.balances} onChange={handleAccordionChange('balances')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceIcon sx={{ fontSize: 20, color: '#0288d1' }} />
                                <Typography variant="subtitle1" fontWeight="600">Opening Balances</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="CashOpeningBalance"
                                        type="number"
                                        value={formData.CashOpeningBalance}
                                        onChange={handleChange}
                                        label="Cash Opening Balance"
                                        placeholder="0.00"
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="CashOpeningDate"
                                        type="date"
                                        value={formData.CashOpeningDate}
                                        onChange={handleChange}
                                        label="Cash Opening Date"
                                        InputLabelProps={{ shrink: true }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="StockOpeningBalance"
                                        type="number"
                                        value={formData.StockOpeningBalance}
                                        onChange={handleChange}
                                        label="Stock Opening Balance"
                                        placeholder="0.00"
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name="StockOpeningDate"
                                        type="date"
                                        value={formData.StockOpeningDate}
                                        onChange={handleChange}
                                        label="Stock Opening Date"
                                        InputLabelProps={{ shrink: true }}
                                        sx={compactInputStyle}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* E-Invoicing Details */}
                    <Accordion expanded={expandedSections.einvoicing} onChange={handleAccordionChange('einvoicing')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CloudIcon sx={{ fontSize: 20, color: '#689f38' }} />
                                <Typography variant="subtitle1" fontWeight="600">E-Invoicing Details</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.IsEInvoicingEnabled}
                                                        onChange={handleChange}
                                                        name="IsEInvoicingEnabled"
                                                    />
                                                }
                                                label="E-Invoicing Enabled"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.IsEWaybillEnabled}
                                                        onChange={handleChange}
                                                        name="IsEWaybillEnabled"
                                                    />
                                                }
                                                label="E-Waybill Enabled"
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="EinvoicePortalUserName"
                                            value={formData.EinvoicePortalUserName}
                                            onChange={handleChange}
                                            label="E-invoice Portal Username"
                                            placeholder="Portal username"
                                            inputProps={{ maxLength: 100 }}
                                            sx={compactInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="EinvoicePortalPassword"
                                            type="password"
                                            value={formData.EinvoicePortalPassword}
                                            onChange={handleChange}
                                            label="E-invoice Portal Password"
                                            placeholder="Portal password"
                                            inputProps={{ maxLength: 100 }}
                                            sx={compactInputStyle}
                                        />
                                    </Grid>
                                </Grid>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="myGSTcafeCustomerID"
                                            value={formData.myGSTcafeCustomerID}
                                            onChange={handleChange}
                                            label="myGST Cafe Customer ID"
                                            placeholder="Customer ID"
                                            inputProps={{ maxLength: 100 }}
                                            sx={compactInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="myGSTcafeApiID"
                                            value={formData.myGSTcafeApiID}
                                            onChange={handleChange}
                                            label="myGST Cafe API ID"
                                            placeholder="API ID"
                                            inputProps={{ maxLength: 100 }}
                                            sx={compactInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="myGSTcafeApiSecret"
                                            type="password"
                                            value={formData.myGSTcafeApiSecret}
                                            onChange={handleChange}
                                            label="myGST Cafe API Secret"
                                            placeholder="API Secret"
                                            inputProps={{ maxLength: 100 }}
                                            sx={compactInputStyle}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>
            
            <Box sx={{ 
                p: 2, 
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                bgcolor: '#f5f5f5'
            }}>
                <Button 
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSaving || !isFormValid}
                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                >
                    {isSaving ? 'Creating...' : 'Create Branch'}
                </Button>
            </Box>
        </Dialog>
    );
};

export default BranchCreateModal;